/**
 * FL-246: 管理者向けドライバースケジュール管理ページ
 *
 * ドライバーの週間稼働スケジュールを管理するメインページ
 * - 複数ドライバー・複数週の一括表示
 * - スケジュール登録・編集・削除
 * - 一括承認・キャンセル機能
 * - フィルター機能（ドライバー、ステータス、日付）
 *
 * Phase 6 実装：Supabase統合
 * 責務：
 * - Supabaseからスケジュール・ドライバーデータを取得
 * - データソースを DriverScheduleManager コンポーネントに連携
 * - CRUD操作のハンドラー実装
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useDateContext } from '@/hooks/useDate';
import { DriverSchedule } from '@/types/schedule';
import { Role } from '@/types';
import { DriverScheduleManager } from '@/components/admin/DriverScheduleManager';
import {
  updateDriverSchedule,
  getCompanySchedules,
  approveSchedule,
  cancelSchedule,
  getCompanyTransporters,
} from '@/services/driverScheduleService';
import logger from '@/utils/logger';
import { PageTitle } from '@/components/ui';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface Driver {
  id: string;
  name: string;
}

/**
 * 管理者向けドライバースケジュール管理ページ
 */
const ScheduleManagementPage: React.FC = () => {
  const { user } = useAuth();
  const { selectedDate, goToPreviousDay, goToNextDay, goToToday } = useDateContext();

  // ドライバースケジュール・マスターデータのstate
  const [schedules, setSchedules] = useState<DriverSchedule[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Supabaseからスケジュール・ドライバーデータを取得
   *
   * Phase 6.5 実装：Supabase連携でデータを取得
   * - driverScheduleService.getCompanySchedules() から会社のスケジュール取得
   * - スケジュールから陸送員情報を抽出
   */
  const loadScheduleData = useCallback(async () => {
    if (!user?.companyId) {
      logger.warn('ScheduleManagementPage: companyId not found');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Phase 6.5: Supabase連携実装（selectedDate でフィルタリング）
      logger.info('ScheduleManagementPage: Starting data load', {
        companyId: user.companyId,
        selectedDate,
      });

      const scheduleData = await getCompanySchedules(user.companyId, selectedDate);
      logger.info(
        `ScheduleManagementPage: Loaded ${scheduleData.length} schedules for date ${selectedDate}`
      );

      // 🔍 [DEBUG] サンプル陸送員Cのスケジュールを確認
      const transporter_c_schedule = scheduleData.find(
        s => s.landTransporterId === 'a998fdf5-4933-4780-8c02-509035a32b93'
      );
      console.log('🔍 [DEBUG] transporter_c_schedule:', transporter_c_schedule);
      console.log('🔍 [DEBUG] All scheduleData:', scheduleData);

      // Phase 6.5: company_land_transporters から陸送員名を取得
      logger.info('ScheduleManagementPage: Fetching company transporters...');
      const transporterMap = await getCompanyTransporters(user.companyId);
      logger.info(`ScheduleManagementPage: Loaded ${transporterMap.size} transporters`, {
        transporterIds: Array.from(transporterMap.keys()),
      });
      console.log('🔍 [DEBUG] transporterMap:', transporterMap);

      // ✅ 修正: 陸送員マスターから全ドライバーを取得
      // スケジュール有無に関わらず、company_land_transporters から全ドライバーを確保
      const allDrivers = Array.from(transporterMap.entries()).map(([id, name]) => ({
        id,
        name,
      }));

      setSchedules(scheduleData);
      setDrivers(allDrivers); // スケジュールの有無に関わらず全ドライバーをセット
      logger.info('ScheduleManagementPage: Data load completed successfully');
      console.log('🔍 [DEBUG] Final schedules state:', scheduleData);
      console.log('🔍 [DEBUG] Final drivers state:', allDrivers);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '不明なエラーが発生しました';
      const errorDetails =
        err instanceof Error
          ? {
              name: err.name,
              message: err.message,
              stack: err.stack,
            }
          : { error: String(err) };

      logger.error('ScheduleManagementPage: Failed to load schedule data', {
        error: errorMessage,
        details: errorDetails,
        companyId: user.companyId,
        selectedDate,
      });
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [user?.companyId, selectedDate]);

  /**
   * コンポーネントマウント時にデータ取得
   * ⚠️ IMPORTANT: ユーザーが完全に認証された後に呼び出す（Supabase sessionの確立を待つ）
   */
  useEffect(() => {
    // ユーザーが存在し、ページへのアクセス権限がある場合のみデータ取得
    // 権限チェック: スーパー管理者・管理者・ディスパッチャーのみ
    const hasPermission =
      !!user &&
      (user.role === Role.SUPER_ADMIN ||
        user.role === Role.ADMIN ||
        user.role === Role.DISPATCHER) &&
      !!user.companyId;

    if (hasPermission) {
      // セッション確立を待つため、短い遅延を追加
      // (localStorage から認証状態が復元されるまでの時間を確保)
      const timeoutId = setTimeout(() => {
        loadScheduleData();
      }, 500);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [user, loadScheduleData]);

  /**
   * スケジュール更新ハンドラー
   *
   * Phase 6.5 実装：Supabase連携で更新を実行
   * - driverScheduleService.updateSchedule() でスケジュール更新
   * - 成功時はローカルstate反映
   */
  const handleScheduleUpdate = useCallback(
    async (scheduleId: string, updates: Partial<DriverSchedule>) => {
      try {
        const updated = await updateDriverSchedule(scheduleId, updates);
        logger.info(`ScheduleManagementPage: Schedule ${scheduleId} updated`, updated);

        // ローカルstateに反映
        setSchedules(prev =>
          prev.map(schedule => (schedule.id === scheduleId ? updated : schedule))
        );
      } catch (err) {
        logger.error('ScheduleManagementPage: Failed to update schedule', err);
        setError(err instanceof Error ? err.message : 'スケジュール更新に失敗しました');
      }
    },
    []
  );

  /**
   * 一括承認ハンドラー
   *
   * Phase 6.5 実装：Supabase連携で一括承認を実行
   * - driverScheduleService.approveSchedule() で複数スケジュールを承認
   * - 成功時はローカルstate反映
   */
  const handleBatchApprove = useCallback(async (scheduleIds: string[]) => {
    try {
      // 複数スケジュールを並列承認
      const approvedSchedules = await Promise.all(scheduleIds.map(id => approveSchedule(id)));
      logger.info(`ScheduleManagementPage: Batch approved ${approvedSchedules.length} schedules`);

      // ローカルstateに反映
      const approvedMap = new Map(approvedSchedules.map(s => [s.id, s]));
      setSchedules(prev => prev.map(schedule => approvedMap.get(schedule.id) || schedule));
    } catch (err) {
      logger.error('ScheduleManagementPage: Failed to batch approve schedules', err);
      setError(err instanceof Error ? err.message : '一括承認に失敗しました');
    }
  }, []);

  /**
   * 一括キャンセルハンドラー
   *
   * Phase 6.5 実装：Supabase連携で一括キャンセルを実行
   * - driverScheduleService.cancelSchedule() で複数スケジュールをキャンセル
   * - 成功時はローカルstate反映
   */
  const handleBatchCancel = useCallback(async (scheduleIds: string[], reason: string) => {
    try {
      // 複数スケジュールを並列キャンセル
      const cancelledSchedules = await Promise.all(
        scheduleIds.map(id => cancelSchedule(id, reason))
      );
      logger.info(
        `ScheduleManagementPage: Batch cancelled ${cancelledSchedules.length} schedules`,
        { reason }
      );

      // ローカルstateに反映
      const cancelledMap = new Map(cancelledSchedules.map(s => [s.id, s]));
      setSchedules(prev => prev.map(schedule => cancelledMap.get(schedule.id) || schedule));
    } catch (err) {
      logger.error('ScheduleManagementPage: Failed to batch cancel schedules', err);
      setError(err instanceof Error ? err.message : '一括キャンセルに失敗しました');
    }
  }, []);

  /**
   * 権限チェック（管理者・ディスパッチャーのみ表示）
   * Phase 6.5 実装：ロール別アクセス制御
   * - スーパー管理者（super_admin）: 全スケジュール管理可能
   * - 管理者（admin）: 全スケジュール管理可能
   * - ディスパッチャー（dispatcher）: スケジュール管理可能
   */
  const canManageSchedules =
    !!user &&
    (user.role === Role.SUPER_ADMIN || user.role === Role.ADMIN || user.role === Role.DISPATCHER);

  if (!canManageSchedules) {
    return (
      <div className='p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg'>
        <h2 className='text-lg font-semibold text-yellow-900 dark:text-yellow-200'>
          アクセス権限がありません
        </h2>
        <p className='text-sm text-yellow-700 dark:text-yellow-300 mt-2'>
          このページへのアクセス権限を持っていません。管理者にお問い合わせください。
        </p>
      </div>
    );
  }

  return (
    <div className='space-y-4 p-4'>
      {/* ページタイトル */}
      <PageTitle
        title='ドライバースケジュール管理'
        subtitle='複数ドライバーのスケジュール確認・承認'
      />

      {/* 日付ナビゲーション */}
      <div className='flex items-center justify-center gap-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg'>
        <button
          onClick={goToPreviousDay}
          className='p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-lg transition-colors'
          title='前日'
        >
          <ChevronLeftIcon className='h-5 w-5 text-gray-600 dark:text-gray-400' />
        </button>

        <div className='flex flex-col items-center'>
          <span className='text-sm text-gray-600 dark:text-gray-400'>表示日</span>
          <span className='text-lg font-semibold text-gray-900 dark:text-white'>
            {selectedDate}
          </span>
        </div>

        <button
          onClick={goToNextDay}
          className='p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-lg transition-colors'
          title='翌日'
        >
          <ChevronRightIcon className='h-5 w-5 text-gray-600 dark:text-gray-400' />
        </button>

        <button
          onClick={goToToday}
          className='px-4 py-2 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors'
        >
          今日
        </button>
      </div>

      {/* エラーメッセージ表示 */}
      {error && (
        <div className='p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg'>
          <h3 className='text-sm font-semibold text-red-900 dark:text-red-200'>
            エラーが発生しました
          </h3>
          <p className='text-sm text-red-700 dark:text-red-300 mt-2'>{error}</p>
          <button
            onClick={() => setError(null)}
            className='mt-3 px-3 py-1 bg-red-200 dark:bg-red-800 text-red-900 dark:text-red-200 rounded text-sm hover:bg-red-300 dark:hover:bg-red-700'
          >
            閉じる
          </button>
        </div>
      )}

      {/* ローディング表示 */}
      {isLoading && (
        <div className='p-8 text-center'>
          <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 dark:border-blue-400'></div>
          <p className='text-gray-600 dark:text-gray-400 mt-2'>スケジュールデータを読み込み中...</p>
        </div>
      )}

      {/* メインコンポーネント */}
      {!isLoading && (
        <DriverScheduleManager
          selectedDate={selectedDate}
          schedules={schedules}
          drivers={drivers}
          onScheduleUpdate={handleScheduleUpdate}
          onBatchApprove={handleBatchApprove}
          onBatchCancel={handleBatchCancel}
        />
      )}
    </div>
  );
};

export default ScheduleManagementPage;
